import './styles/index.css';

import { initI18n, t } from './core/i18n.js';
import { defineRoutes, startRouter, onNavigate } from './core/router.js';
import { applyRouteMeta } from './core/seo.js';
import { canonicalPath, parsePath } from './core/url.js';
import { site } from './data/site.js';
import { ROUTES } from './routes.js';
import { NotFoundPage } from './pages/notFound.js';

/* ── Mobile menu ──────────────────────────────────────────────────────────── */

const setPageInert = (open) => {
  const main = document.getElementById('main');
  const footer = document.querySelector('.footer');
  if (main) main.inert = open;
  if (footer) footer.inert = open;
};

const closeMenu = () => {
  const bar = document.querySelector('.topbar');
  const toggle = bar?.querySelector('.topbar__toggle');
  document.body.classList.remove('menu-open');
  setPageInert(false);
  if (!bar || !toggle) return;
  bar.classList.remove('is-open');
  toggle.setAttribute('aria-expanded', 'false');
  toggle.setAttribute('aria-label', t('nav.menu.open'));
};

const wireMenu = () => {
  const bar = document.querySelector('.topbar');
  const toggle = bar?.querySelector('.topbar__toggle');
  if (!toggle) return;
  toggle.addEventListener('click', () => {
    const open = !bar.classList.contains('is-open');
    bar.classList.toggle('is-open', open);
    document.body.classList.toggle('menu-open', open);
    setPageInert(open);
    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute('aria-label', t(open ? 'nav.menu.close' : 'nav.menu.open'));
    if (open) bar.querySelector('.topbar__nav a')?.focus();
  });
};

/* ── Scroll reveal ────────────────────────────────────────────────────────── */

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let observer = null;
const wireReveal = () => {
  const targets = document.querySelectorAll('[data-reveal]');
  if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
    document.documentElement.classList.remove('js-anim');
    return;
  }
  document.documentElement.classList.add('js-anim');
  observer?.disconnect();
  observer = new IntersectionObserver(
    (entries, self) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        self.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
  );
  targets.forEach((el, i) => {
    el.style.setProperty('--reveal-delay', `${Math.min(i, 8) * 45}ms`);
    observer.observe(el);
  });
};

/* ── Language switch ──────────────────────────────────────────────────────── */

const wireLanguage = () => {
  const select = document.querySelector('[data-lang-select]');
  if (!select) return;
  select.addEventListener('change', () => {
    select.disabled = true;
    window.location.assign(select.value);
  });
};

/* ── Catalogue filters ────────────────────────────────────────────────────── */

const wireCatalogue = () => {
  const root = document.querySelector('[data-catalogue]');
  if (!root) return;

  const buttons = [...root.querySelectorAll('[data-filter]')];
  const cards = [...root.querySelectorAll('[data-card]')];
  const status = root.querySelector('[data-filter-status]');
  if (!buttons.length || !cards.length) return;

  const apply = (filter) => {
    let visible = 0;
    buttons.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.filter === filter));
    });
    cards.forEach((card) => {
      const match = filter === 'all' || card.dataset.genre === filter;
      card.hidden = !match;
      if (match) {
        card.classList.add('is-revealed');
        visible += 1;
      }
    });
    if (status) status.textContent = t('games.filter.count', { count: visible });
  };

  buttons.forEach((button) => {
    button.addEventListener('click', () => apply(button.dataset.filter));
  });
};

/* ── Contact form ─────────────────────────────────────────────────────────── */

const endpoint = () =>
  ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? site.contactEndpoint.development
    : site.contactEndpoint.production;

const wireContactForm = () => {
  const form = document.getElementById('contactForm');
  if (!form) return;

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const status = form.querySelector('#formStatus');
    const button = form.querySelector('#submitBtn');

    if (!form.checkValidity()) {
      status.className = 'form__status is-error';
      status.textContent = t('contact.form.required');
      form.reportValidity();
      return;
    }

    button.disabled = true;
    status.className = 'form__status is-pending';
    status.textContent = t('contact.form.sending');

    try {
      const response = await fetch(endpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.fromEntries(new FormData(form))),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      form.reset();
      status.className = 'form__status is-success';
      status.textContent = t('contact.form.success');
    } catch (error) {
      console.error('Contact form failed', error);
      status.className = 'form__status is-error';
      status.textContent = t('contact.form.error');
    } finally {
      button.disabled = false;
    }
  });
};

/* ── Article charts ───────────────────────────────────────────────────────── */

/**
 * Hover and keyboard read-out for line charts. The prerendered chart is
 * complete without it; this only adds a cursor, one marker per series and a
 * tooltip. The data table in every figure stays the accessible equivalent.
 */
const nearestIndex = (points, x) =>
  points.reduce((best, point, index) => (Math.abs(point[0] - x) < Math.abs(points[best][0] - x) ? index : best), 0);

const wireLineChart = (root) => {
  const plot = root.querySelector('.viz-line__plot');
  const cursor = root.querySelector('.viz-line__cursor');
  const tip = root.querySelector('.viz-line__tip');
  if (!plot || !cursor || !tip) return;

  const data = JSON.parse(root.dataset.vizLine);
  const locale = `${document.documentElement.lang}-u-nu-latn`;
  const xFormat = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const yFormat = new Intl.NumberFormat(locale, {
    minimumFractionDigits: data.y.decimals,
    maximumFractionDigits: data.y.decimals,
  });
  const fx = (x) => (x - data.x.min) / (data.x.max - data.x.min);
  const fy = (y) => 1 - (y - data.y.min) / (data.y.max - data.y.min);
  const [lead] = data.series;
  const markers = data.series.map((series) => {
    const marker = document.createElement('span');
    marker.className = `viz-line__marker is-${series.tone}`;
    marker.hidden = true;
    plot.append(marker);
    return marker;
  });
  let index = lead.points.length - 1;

  const show = (next) => {
    index = Math.max(0, Math.min(lead.points.length - 1, next));
    const [x] = lead.points[index];
    cursor.style.setProperty('--x', fx(x));
    tip.style.setProperty('--x', fx(x));
    tip.classList.toggle('is-end', fx(x) > 0.55);

    const head = document.createElement('span');
    head.className = 'viz-line__tip-head';
    head.textContent = `${xFormat.format(x)} ${data.x.unit}`;
    const rows = data.series.map((series, position) => {
      const point = series.points[nearestIndex(series.points, x)];
      markers[position].style.setProperty('--x', fx(point[0]));
      markers[position].style.setProperty('--y', fy(point[1]));
      markers[position].hidden = false;

      const row = document.createElement('span');
      row.className = `viz-line__tip-row is-${series.tone}`;
      const key = document.createElement('span');
      key.className = 'viz-line__tip-key';
      const value = document.createElement('b');
      value.textContent = yFormat.format(point[1]);
      row.append(key, series.name, value);
      return row;
    });
    tip.replaceChildren(head, ...rows);
    cursor.hidden = false;
    tip.hidden = false;
  };

  const hide = () => {
    cursor.hidden = true;
    tip.hidden = true;
    markers.forEach((marker) => {
      marker.hidden = true;
    });
  };

  const fromPointer = (event) => {
    const rect = plot.getBoundingClientRect();
    const x = data.x.min + ((event.clientX - rect.left) / rect.width) * (data.x.max - data.x.min);
    show(nearestIndex(lead.points, x));
  };

  plot.addEventListener('pointermove', fromPointer);
  plot.addEventListener('pointerdown', fromPointer);
  plot.addEventListener('pointerleave', hide);
  plot.addEventListener('focus', () => show(index));
  plot.addEventListener('blur', hide);
  plot.addEventListener('keydown', (event) => {
    const moves = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: lead.points.length - 1 };
    if (!(event.key in moves)) return;
    event.preventDefault();
    show(moves[event.key]);
  });
};

const wireCharts = () => {
  document.querySelectorAll('[data-viz-line]').forEach(wireLineChart);
};

/* ── Boot ─────────────────────────────────────────────────────────────────── */

const afterRender = (path, route) => {
  closeMenu();
  applyRouteMeta(route, path);
  wireMenu();
  wireLanguage();
  wireReveal();
  wireCatalogue();
  wireContactForm();
  wireCharts();
};

const boot = async () => {
  // `#/games` was the old canonical form; map it to `/games/` before rendering.
  const legacyHash = window.location.hash.startsWith('#/') ? window.location.hash.slice(1) : null;
  const initial = parsePath(legacyHash ?? window.location.pathname);
  const knownRoute = ROUTES.some((route) => route.path === initial.path);
  const target = canonicalPath(initial);
  const search = window.location.search;
  const hash = legacyHash ? '' : window.location.hash;
  if (knownRoute && (legacyHash || window.location.pathname !== target)) {
    window.history.replaceState(null, '', `${target}${search}${hash}`);
  }

  await initI18n(initial.locale);
  defineRoutes(ROUTES);

  onNavigate(afterRender);
  window.addEventListener('resize', () => {
    if (window.innerWidth > 980) closeMenu();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const wasOpen = document.querySelector('.topbar')?.classList.contains('is-open');
    closeMenu();
    if (wasOpen) document.querySelector('.topbar__toggle')?.focus();
  });

  await startRouter({
    mount: document.getElementById('app'),
    fallback: NotFoundPage,
    path: initial.path,
  });
  document.documentElement.classList.remove('is-booting');
  window.dispatchEvent(new CustomEvent('netbyte:ready'));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
